import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from 'lib/api/apiClient';
import { USER_URLS } from 'lib/api/endpoints';

export const fetchUsers = createAsyncThunk('users/fetchUsers', async (_, thunkAPI) => {
  try {
    const response = await apiClient.get(USER_URLS.getAllUsers);
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to fetch users');
  }
});

export const updateUserRole = createAsyncThunk(
  'users/updateUserRole',
  async ({ userId, role }, thunkAPI) => {
    try {
      await apiClient.put(USER_URLS.updateUser(userId), { role });
      return { userId, role };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to update role');
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'users/updateUserStatus',
  async ({ userId, status }, thunkAPI) => {
    try {
      await apiClient.put(USER_URLS.updateUser(userId), { status });
      return { userId, status };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to update status');
    }
  }
);

export const importUsers = createAsyncThunk('users/importUsers', async (file, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(USER_URLS.importUsers, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response?.data?.data ?? response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.detail || 'Failed to import users');
  }
});

const userSlice = createSlice({
  name: 'users',
  initialState: {
    users: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearUserError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        const user = state.users.find((u) => u.id === action.payload.userId);
        if (user) user.role = action.payload.role;
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        const user = state.users.find((u) => u.id === action.payload.userId);
        if (user) user.is_active = action.payload.status;
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(importUsers.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.users = action.payload;
        }
      })
      .addCase(importUsers.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearUserError } = userSlice.actions;
export default userSlice.reducer;
